import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Copy, Download, FileDown, KeyRound, Loader2, MessageSquareText, PackageOpen, ShieldOff } from 'lucide-react';
import { useStore } from '@/contexts/StoreContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { digitalService, pedacosDoConteudo, tamanhoLegivel, type ItemDaBiblioteca } from '@/lib/services/digital.service';

const LOCALE: Record<string, string> = { pt: 'pt-BR', en: 'en-US', es: 'es-ES' };
const ICONE = { FILE: FileDown, LICENSE_KEY: KeyRound, COMMAND: PackageOpen } as const;

/**
 * "Minhas compras": o que o comprador recebeu na loja de produtos digitais.
 *
 * Location: src/components/digital
 * Why: é onde a entrega chega. Arquivo sai por um link assinado que vale 5
 * minutos (pedido na hora do clique, e cada clique conta no limite da
 * compra); a chave e a mensagem da loja vêm só para o dono. A mensagem é
 * texto: os links viram `<a>`, o resto continua texto, nada vira HTML.
 */
export function MinhasCompras() {
  const { t, i18n } = useTranslation();
  const { store } = useStore();
  const locale = LOCALE[i18n.language?.slice(0, 2)] ?? 'pt-BR';
  const [itens, setItens] = useState<ItemDaBiblioteca[] | null>(null);
  const [baixando, setBaixando] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    if (!store?.id) return;
    try {
      setItens(await digitalService.library(store.id));
    } catch {
      setItens([]);
      toast.error(t('store.digital.library.load_error'));
    }
  }, [store?.id, t]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const baixar = async (item: ItemDaBiblioteca) => {
    if (!store?.id) return;
    setBaixando(item.accessId);
    try {
      const link = await digitalService.download(store.id, item.accessId);
      // O link é do armazenamento e já manda baixar (Content-Disposition: attachment).
      window.location.assign(link.url);
      setItens((antes) => antes?.map((i) => (i.accessId === item.accessId ? { ...i, downloads: i.downloads + 1 } : i)) ?? null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('store.digital.library.download_error'));
    } finally {
      setBaixando(null);
    }
  };

  if (itens === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (itens.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border py-14 text-center" data-testid="biblioteca-vazia">
        <PackageOpen className="h-10 w-10 text-muted-foreground/50" />
        <p className="text-foreground font-medium">{t('store.digital.library.empty_title')}</p>
        <p className="max-w-sm text-sm text-muted-foreground">{t('store.digital.library.empty_desc')}</p>
        <Button asChild variant="outline" className="mt-2">
          <Link to="/loja">{t('store.digital.library.go_shop')}</Link>
        </Button>
      </div>
    );
  }

  return (
    <ul className="space-y-4" data-testid="biblioteca">
      {itens.map((item) => {
        const Icone = ICONE[item.kind] ?? PackageOpen;
        const esgotou = item.downloadLimit !== null && item.downloads >= item.downloadLimit;
        return (
          <li key={item.accessId} className="rounded-2xl border border-border bg-card p-4 sm:p-5" data-acesso={item.accessId} data-tipo={item.kind}>
            <div className="flex flex-wrap items-start gap-4">
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-muted">
                {item.productImage ? (
                  <img src={item.productImage} alt="" className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <Icone className="h-7 w-7 text-muted-foreground/60" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1 space-y-1">
                <p className="flex flex-wrap items-center gap-2 font-semibold text-foreground">
                  <span className="break-words">{item.productName}</span>
                  <Badge variant="outline" className="font-normal">
                    <Icone className="mr-1 h-3 w-3" />
                    {t(`store.digital.kind.${item.kind}`)}
                  </Badge>
                  {item.revoked && (
                    <Badge variant="destructive" className="font-normal">
                      <ShieldOff className="mr-1 h-3 w-3" />
                      {t('store.digital.library.revoked')}
                    </Badge>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('store.digital.library.bought_at', { date: new Date(item.purchasedAt).toLocaleDateString(locale) })}
                </p>
              </div>
            </div>

            {item.revoked ? (
              <p className="mt-4 text-sm text-muted-foreground">{t('store.digital.library.revoked_desc')}</p>
            ) : item.kind === 'FILE' ? (
              <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl bg-muted/40 p-3">
                {item.file ? (
                  <>
                    <div className="min-w-0 flex-1 text-sm">
                      <p className="truncate font-medium text-foreground">{item.file.fileName}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('store.digital.library.version', { v: item.file.version })} · {tamanhoLegivel(item.file.size, locale)}
                        {' · '}
                        {item.downloadLimit !== null
                          ? t('store.digital.library.downloads_of', { n: item.downloads, limit: item.downloadLimit })
                          : t('store.digital.library.downloads_unlimited')}
                      </p>
                      {item.file.notes && <p className="mt-1 whitespace-pre-line break-words text-xs text-muted-foreground">{item.file.notes}</p>}
                    </div>
                    <Button onClick={() => void baixar(item)} disabled={esgotou || baixando === item.accessId} data-testid="baixar">
                      {baixando === item.accessId ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                      {esgotou ? t('store.digital.library.limit_reached') : t('store.digital.library.download')}
                    </Button>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('store.digital.library.file_pending')}</p>
                )}
              </div>
            ) : item.kind === 'LICENSE_KEY' ? (
              <div className="mt-4">
                {item.licenseKey ? <ChaveCopiavel valor={item.licenseKey} /> : <p className="text-sm text-muted-foreground">{t('store.digital.library.key_pending')}</p>}
              </div>
            ) : null}

            {!item.revoked && item.message && (
              <div className="mt-3 rounded-xl border border-border/60 p-3" data-testid="mensagem-da-loja">
                <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <MessageSquareText className="h-3.5 w-3.5" />
                  {t('store.digital.library.message')}
                </p>
                <div className="whitespace-pre-wrap break-words text-sm text-foreground">
                  {pedacosDoConteudo(item.message).map((p, i) =>
                    p.tipo === 'link' ? (
                      <a key={i} href={p.valor} target="_blank" rel="noopener noreferrer nofollow" className="break-all text-primary underline underline-offset-2">
                        {p.valor}
                      </a>
                    ) : (
                      <span key={i}>{p.valor}</span>
                    ),
                  )}
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ChaveCopiavel({ valor }: { valor: string }) {
  const { t } = useTranslation();
  const { copy, copied } = useCopyToClipboard();
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl bg-muted/40 p-3">
      <code className="min-w-0 flex-1 break-all font-mono text-sm text-foreground" data-testid="chave-de-licenca">{valor}</code>
      <Button type="button" variant="outline" size="sm" onClick={() => copy(valor)}>
        {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
        {copied ? t('store.digital.library.copied') : t('store.digital.library.copy')}
      </Button>
    </div>
  );
}
